/**
 * Shared formatting for the eval viewer.
 *
 * Every score is a normalized float in [0, 1]. A group chooses how it reads:
 * `percent` (73.5%) for accuracy/pass-rate metrics, or `decimal` (0.735) for
 * C-index / AUC / correlation, where a percentage is semantically wrong.
 */

export type ScoreFormat = 'percent' | 'decimal'

export function formatScore(score: number | null, format: ScoreFormat = 'percent'): string {
  if (score === null) return '—'
  return format === 'decimal' ? score.toFixed(3) : `${(score * 100).toFixed(1)}%`
}

/** Axis-tick label for a raw [0,1] score under the group's chosen format. */
export function formatAxisTick(score: number, format: ScoreFormat = 'percent'): string {
  return format === 'decimal' ? score.toFixed(2) : `${Math.round(score * 100).toString()}%`
}

export function formatVariant(variant: string): string {
  return variant || 'default (unlabeled)'
}

export function formatRelative(iso: string | null): string {
  if (iso === null) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatDuration(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return '—'
  const seconds = (new Date(finishedAt).getTime() - new Date(startedAt).getTime()) / 1000
  if (seconds < 0) return '—'
  if (seconds < 60) return `${Math.round(seconds).toString()}s`
  const minutes = seconds / 60
  if (minutes < 60) return `${Math.round(minutes).toString()}m`
  return `${(minutes / 60).toFixed(1)}h`
}
